"use client"

import React, { useState, useTransition } from "react"
import { useTranslations } from "next-intl"
import { formatPrice } from "@/lib/helper"
import { useRouter } from "next/navigation"
import { approveRequisition, rejectRequisition } from "@/actions/requisitionActions"
import { Button } from "@/components/ui/components/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/components/alert-dialog"
import { Textarea } from "@/components/ui/components"
import { toast } from "sonner"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { APP_B2B_ACCOUNT_OWNER, APP_B2B_APPROVER } from "@/constants"

import { AccountContext } from "@/lib/storefront/account/account-service"
import { hasAnyRole } from "@/lib/role-helper"
import { IncludedProduct, Requisition, RequisitionLineItem, RequisitionPayment } from "@/lib/intershop/interfaces/requisition/requisition"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { PaymentMethodBaseData } from "@/lib/intershop/interfaces/payment-method/payment-method"

interface AccountApprovalDetailClientProps {
  accountContext: AccountContext
  approval: Requisition | undefined
  blok?: any
  invoiceToAddressDetail?: ExtendedAddressRO | undefined
  shipToAddressDetail?: ExtendedAddressRO | undefined
}

export default function AccountApprovalDetailClient(props: AccountApprovalDetailClientProps) {
  const { approval } = props
  const data = approval?.data
  const included = approval?.included ?? {}
  const isoLocale = props.accountContext?.isoLocale
  const t = useTranslations("storefront")

  function mapShippingMethod(code?: string) {
    if (!code) return "-"
    const map: Record<string, string> = {
      DE_STANDARD: t("approvals.detail.shipping_method.standard"),
      STANDARD: t("approvals.detail.shipping_method.standard"),
      EXPRESS: t("approvals.detail.shipping_method.express"),
    }
    return map[code] ?? code
  }

  const totals = data?.totals ?? {}
  const itemTotalNet = totals?.itemTotal?.net
  const grand = totals?.grandTotal
  const shipping = totals?.shippingTotal
  const mwstValue = grand?.gross?.value != null && itemTotalNet?.value != null ? Math.max(0, Number(grand.gross?.value) - Number(itemTotalNet.value)) : undefined
  const mwstText = mwstValue != null && grand?.gross?.currency ? formatPrice(isoLocale, mwstValue, String(grand?.gross?.currency)) : undefined

  const buyerName =
    [data?.userInformation?.firstName, data?.userInformation?.lastName].filter(Boolean).join(" ") ||
    data?.userInformation?.login ||
    "-"
  function formatShortDate(value?: string | number | Date) {
    if (!value) return "-"
    const d = new Date(value)
    if (isNaN(d.getTime())) return "-"
    return `${d.getDate()}.${d.getMonth() + 1}.${(d.getFullYear() % 100).toString()}`
  }
  const requisitionNo = String(data?.requisitionNo ?? data?.id ?? "-")
  const createdShort = formatShortDate(data?.creationDate)
  const grossTextHeader =
    (grand?.gross?.value != null && grand?.gross?.currency
      ? formatPrice(isoLocale, Number(grand.gross.value), String(grand.gross.currency))
      : grand?.gross?.value != null && grand?.gross?.currency
      ? formatPrice(isoLocale, Number(grand.gross.value), String(grand.gross.currency))
      : data?.totalGross?.value != null && data?.totalGross?.currency
      ? formatPrice(isoLocale, Number(data.totalGross.value), String(data.totalGross.currency))
      : undefined) ?? "-"
  const statusCode = data?.approvalStatus?.statusCode
  const statusLabel = data?.approvalStatus?.status ?? statusCode ?? "-"
  const statusClass =
    statusCode === "APPROVED"
      ? "bg-emerald-100 text-emerald-800"
      : statusCode === "REJECTED"
      ? "bg-rose-100 text-rose-800"
      : "bg-blue-100 text-blue-800"
  const isPending =
    String(statusCode).toUpperCase() === "PENDING" ||
    ["pending", "offen"].includes(String(statusLabel || "").toLowerCase())
  const rejectionComment =
    data?.approvalStatus?.approvalComment as string | undefined

  if (!data) {
    return (
      <div className="rounded-sm border border-gray-200 bg-white p-4">
        {t("approvals.detail.empty_data")}
      </div>
    )
  }

  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectComment, setRejectComment] = useState("")
  const canManageApproval = hasAnyRole(props.accountContext, [APP_B2B_APPROVER, APP_B2B_ACCOUNT_OWNER]) || false

  async function onApprove(formData: FormData) {
    if (!canManageApproval) return
    const res = await approveRequisition(formData)
    if (res?.ok) {
      toast.success(t("approvals.toasts.approve_success"))
      const url = getLinkWithPrefixFromURLAndLocale("/account/approvals/open", props.accountContext.sbLocale)
      startTransition(() => router.push(url))
    } else {
      toast.error(t("approvals.toasts.approve_error"))
      startTransition(() => router.refresh())
    }
  }
  async function onReject(formData: FormData) {
    if (!canManageApproval) return
    const res = await rejectRequisition(formData)
    if (res?.ok) {
      toast.success(t("approvals.toasts.reject_success"))
      const url = getLinkWithPrefixFromURLAndLocale("/account/approvals/open", props.accountContext.sbLocale)
      startTransition(() => router.push(url))
    } else {
      toast.error(t("approvals.toasts.reject_error"))
      startTransition(() => router.refresh())
    }
  }
  async function confirmReject() {
    if (!canManageApproval) return
    if (!rejectComment || rejectComment.trim().length === 0) return
    const fd = new FormData()
    fd.set("customerNo", String(props.accountContext.customerData?.customerNo || ""))
    fd.set(
      "userLogin",
      String(props.accountContext.userData?.email || props.accountContext.userData?.login || "")
    )
    fd.set("requisitionId", String(data?.id || data?.requisitionNo || ""))
    fd.set("approvalComment", rejectComment.trim())
    await onReject(fd)
    setRejectOpen(false)
    setRejectComment("")
  }

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">{t("approvals.detail.title")}</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <div>
          <div className="text-xs text-gray-500">{t("approvals.detail.labels.requisition_no")}</div>
          <div className="font-mono">{requisitionNo}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("approvals.detail.labels.buyer")}</div>
          <div>{buyerName}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("approvals.detail.labels.creation_date")}</div>
          <div>{createdShort}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("approvals.detail.labels.total")}</div>
          <div>{grossTextHeader}</div>
        </div>
        <div className="flex flex-col items-end justify-start gap-1">
          <span className="text-xs text-gray-500">{t("approvals.detail.labels.status")}</span>
          <span className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ${statusClass}`}>{statusLabel}</span>
        </div>
      </div>
      {data.messageToMerchant && (
        <div className="mt-3">
          <div className="text-xs text-gray-500">{t("approvals.detail.labels.message")}</div>
          <div>{data.messageToMerchant}</div>
        </div>
      )}

      {String(statusCode).toUpperCase() === "REJECTED" && rejectionComment && (
        <div className="mt-3 rounded-sm border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <div className="mb-1 font-medium">{t("approvals.detail.labels.rejection_reason")}</div>
          <div className="whitespace-pre-line">{rejectionComment}</div>
        </div>
      )}


    {/* Einkäufer-Genehmigung (Budget) */}
    {data?.userBudgets ? (
      <div className="mt-4 rounded-sm border border-gray-200 bg-white p-4">
        {(() => {
          const ub = data.userBudgets
          const fmt = (m?: { value?: number; currency?: string } | null) =>
            m && m.value != null && m.currency
              ? formatPrice(isoLocale, Number(m.value), String(m.currency))
              : "-"
          const grossV = Number(grand?.gross?.value ?? data?.totalGross?.value ?? 0)
          const orderLimit = ub.orderSpentLimit
          const limitV = Number(orderLimit?.value ?? 0)
          const limitPct = limitV > 0 ? Math.min((grossV / limitV) * 100, 100) : 0
          const monthly = ub.budget
          const budgetV = Number(monthly?.value ?? 0)
          const remaining = Number(ub.remainingBudget?.value ?? budgetV)
          const spentAlready = Math.max(budgetV - remaining, 0)
          const spentIncl = Number(ub.spentBudgetIncludingThisRequisition?.value ?? grossV)
          const spentInclPct = budgetV > 0 ? Math.min((spentIncl / budgetV) * 100, 100) : 0
          const pct = (p: number) => `${p.toFixed(1).replace(".", ",")}%`
          return (
            <div className="grid grid-cols-1 gap-3">
              <div className="text-sm font-medium text-gray-800">{t("approvals.detail.buyer_approval.title")}</div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-[max-content_1fr]">
                <div className="text-sm text-gray-700">{t("approvals.detail.buyer_approval.buyer")}</div>
                <div className="text-sm">{buyerName}</div>
              </div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-[max-content_1fr]">
                    <div className="text-sm text-gray-700">{t("approvals.detail.buyer_approval.order_limit")}</div>
                    <div className="text-sm">{fmt(orderLimit)}</div>
              </div>
              <div className="flex justify-start text-xs text-gray-700">{pct(limitPct)}</div>
              <div
                className="h-6 w-56 overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-valuenow={Math.max(0, Math.min(100, limitPct))}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                    className="flex h-full flex-col justify-center bg-primary text-center text-xs whitespace-nowrap text-white transition-[width] duration-500"
                    style={{ width: `${Math.max(0, Math.min(100, limitPct))}%` }}
                    >
                    <p className="p-2">{fmt({ value: grossV, currency: orderLimit?.currency || monthly?.currency || grand?.gross?.currency })}</p>  
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-[max-content_1fr]">
                <div className="text-sm text-gray-700">{t("approvals.detail.buyer_approval.monthly_budget")}</div>
                <div className="text-sm">{fmt(monthly)}</div>
              </div>
              <p className="text-xs text-gray-600">{t("account.users.budget_gross_hint")}</p>
              <div
                className="h-6 w-56 overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-valuenow={Math.max(0, Math.min(100, spentInclPct))}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="flex h-full flex-col justify-center bg-primary text-center text-xs whitespace-nowrap text-white transition-[width] duration-500"
                  style={{ width: `${Math.max(0, Math.min(100, spentInclPct))}%` }}
                >
                  <p className="p-2">{fmt({ value: grossV, currency: monthly?.currency || grand?.gross?.currency })}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-[max-content_1fr]">
                <div className="text-sm text-gray-700">{t("approvals.detail.buyer_approval.already_spent")}</div>
                <div className="text-sm">{fmt({ value: spentAlready, currency: monthly?.currency })}</div>
              </div>
              <div
                className="h-6 w-56 overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-valuenow={Math.max(0, Math.min(100, (spentAlready / (budgetV || 1)) * 100))}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="flex h-full flex-col justify-center bg-primary text-center text-xs whitespace-nowrap text-white transition-[width] duration-500"
                  style={{ width: `${Math.max(0, Math.min(100, (spentAlready / (budgetV || 1)) * 100))}%` }}
                >
                  <p className="p-2">{pct((spentAlready / (budgetV || 1)) * 100)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-[max-content_1fr]">
                <div className="text-sm text-gray-700">{t("approvals.detail.buyer_approval.including_this_order")}</div>
                <div className="text-sm">{fmt({ value: spentIncl, currency: monthly?.currency })}</div>
              </div>
              <div
                className="h-6 w-56 overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-valuenow={Math.max(0, Math.min(100, spentInclPct))}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="flex h-full flex-col justify-center bg-primary text-center text-xs whitespace-nowrap text-white transition-[width] duration-500"
                  style={{ width: `${Math.max(0, Math.min(100, spentInclPct))}%` }}
                >
                  <p className="p-2">{pct(spentInclPct)}</p>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    ) : null}

    {/* Actions between budget and details */}
    {(isPending && canManageApproval) && (
    <div className="mt-3 flex flex-wrap gap-3">
      <Button type="button" variant="outline" disabled={pending || !canManageApproval} onClick={() => setRejectOpen(true)}>
        {pending ? "…" : t("approvals.detail.actions.reject")}
      </Button>
      <form action={onApprove} onSubmit={(e) => { if (!canManageApproval) { e.preventDefault() } }}>
        <input type="hidden" name="customerNo" value={String(props.accountContext.customerData?.customerNo || "")} />
        <input type="hidden" name="userLogin" value={String(props.accountContext.userData?.email || props.accountContext.userData?.login || "")} />
        <input type="hidden" name="requisitionId" value={String(data?.id || data?.requisitionNo || "")} />
        <Button type="submit" disabled={pending || !canManageApproval}>
          {pending ? "…" : t("approvals.detail.actions.approve")}
        </Button>
      </form>
    </div>
    )}
      {/* Details: Addresses, Payments, Line Items */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-sm border border-gray-200 bg-white p-3">
          <h3 className="mb-2 text-sm font-semibold">{t("approvals.detail.addresses.billing")}</h3>
          {props.invoiceToAddressDetail ? (
            <p className="text-sm">
              {props.invoiceToAddressDetail?.companyName1 && (
                <>
                  {props.invoiceToAddressDetail.companyName1}
                  <br />
                </>
              )}
              {props.invoiceToAddressDetail?.companyName2 && (
                <>
                  {props.invoiceToAddressDetail.companyName2}
                  <br />
                </>
              )}
              {props.invoiceToAddressDetail?.addressLine1 && (
                <>
                  {props.invoiceToAddressDetail.addressLine1}
                  <br />
                </>
              )}
              {props.invoiceToAddressDetail?.addressLine2 && (
                <>
                  {props.invoiceToAddressDetail.addressLine2}
                  <br />
                </>
              )}
              {props.invoiceToAddressDetail?.postalCode}&nbsp;{props.invoiceToAddressDetail?.city}
            </p>
          ) : (
            <p className="text-sm text-gray-600">{String(data.invoiceToAddress ?? "-")}</p>
          )}
        </div>

        <div className="rounded-sm border border-gray-200 bg-white p-3">
          <h3 className="mb-2 text-sm font-semibold">{t("approvals.detail.addresses.shipping")}</h3>
          {props.shipToAddressDetail ? (
            <p className="text-sm">
              {props.shipToAddressDetail?.companyName1 && (
                <>
                  {props.shipToAddressDetail.companyName1}
                  <br />
                </>
              )}
              {props.shipToAddressDetail?.companyName2 && (
                <>
                  {props.shipToAddressDetail.companyName2}
                  <br />
                </>
              )}
              {props.shipToAddressDetail?.addressLine1 && (
                <>
                  {props.shipToAddressDetail.addressLine1}
                  <br />
                </>
              )}
              {props.shipToAddressDetail?.addressLine2 && (
                <>
                  {props.shipToAddressDetail.addressLine2}
                  <br />
                </>
              )}
              {props.shipToAddressDetail?.postalCode}&nbsp;{props.shipToAddressDetail?.city}
            </p>
          ) : (
            <p className="text-sm text-gray-600">{String(data.commonShipToAddress ?? "-")}</p>
          )}
        </div>

        <div className="rounded-sm border border-gray-200 bg-white p-3">
          <h3 className="mb-2 text-sm font-semibold">{t("approvals.detail.payment_method.title")}</h3>
          {(() => {
            const pmMethodRaw = included?.payments_paymentMethod
            const pmRaw = included?.payments
            const pmMethods: RequisitionPayment[] = Array.isArray(pmMethodRaw)
              ? pmMethodRaw
              : pmMethodRaw && typeof pmMethodRaw === "object"
                ? Object.values(pmMethodRaw)
                : []
            const payments: RequisitionPayment[] = Array.isArray(pmRaw)
              ? pmRaw
              : pmRaw && typeof pmRaw === "object"
                ? Object.values(pmRaw)
                : []

            if (payments.length > 0 || pmMethods.length > 0) {
              return (
                <ul className="text-sm">
                  {(payments.length > 0 ? payments : pmMethods).map((p: RequisitionPayment, idx: number) => {
                    const methodId = p?.paymentMethod
                    const method =
                      pmMethods.find(m => m?.id === methodId)
                    const methodName = method?.displayName ?? method?.displayName ?? methodId
                    const amount = (p as RequisitionPayment)?.totalAmount
                    const amountText = amount
                      ? formatPrice(isoLocale, Number(amount?.value), String(amount?.currency))
                      : undefined
                    return (
                      <li key={idx}>
                        {methodName}
                        {amountText ? ` – ${amountText}` : ""}
                      </li>
                    )
                  })}
                </ul>
              )
            }
            // fallback: just IDs from data.payments
            return (
              <p className="text-sm">
                {Array.isArray(data.payments) ? data.payments.join(", ") : String(data.payments ?? "-")}
              </p>
            )
          })()}
        </div>

        <div className="rounded-sm border border-gray-200 bg-white p-3">
          <h3 className="mb-2 text-sm font-semibold">{t("approvals.detail.shipping_method.title")}</h3>
          <p className="text-sm">
            {mapShippingMethod(String((included?.lineItems && Object.values(included.lineItems)[0]?.shippingMethod) || data?.commonShippingMethod))}
            {" "}
            {shipping?.gross?.value != null
              ? ` - ${formatPrice(isoLocale, Number(shipping.gross.value), String(shipping.gross.currency))}`
              : shipping?.net?.value != null
              ? ` - ${formatPrice(isoLocale, Number(shipping.net.value), String(shipping.net.currency))}`
              : ""}
          </p>
        </div>

        <div className="rounded-sm border border-gray-200 bg-white p-0 md:col-span-2">
          <div className="border-b bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600">
            {t("approvals.detail.items_header.title")}
            <span className="float-right grid grid-cols-[120px_120px] gap-4 pr-3 max-sm:hidden">
              <span className="text-right">{t("approvals.detail.items_header.price")}</span>
              <span className="text-right">{t("approvals.detail.items_header.total")}</span>
            </span>
          </div>
          {(() => {
            const liRaw = included?.lineItems
            const prodRaw = included?.lineItems_product
            const items: RequisitionLineItem[] = Array.isArray(liRaw)
              ? liRaw
              : liRaw && typeof liRaw === "object"
                ? Object.values(liRaw)
                : []
            const productsMap: Record<string, IncludedProduct> = Array.isArray(prodRaw)
              ? Object.fromEntries(prodRaw.map(p => [p?.sku ?? p?.id, p]))
              : prodRaw && typeof prodRaw === "object"
                ? prodRaw
                : {}

            if (items.length > 0) {
              const rows = items
              return (
                <div className="divide-y">
                  {rows.map((it: RequisitionLineItem, idx: number) => {
                    const prod = productsMap?.[String(it?.product)]
                    const title = prod?.name || it?.id || it?.product || t("approvals.detail.items_header.fallback_position")
                    const sku = prod?.sku
                    const qty = it?.quantity?.value
                    const priceSingle = it?.pricing?.singleBasePrice?.gross ?? it?.pricing?.singleBasePrice?.net
                    const priceSum = it?.pricing?.price?.gross ?? it?.pricing?.price?.net
                    const priceSingleText = priceSingle
                      ? formatPrice(isoLocale, Number(priceSingle?.value), String(priceSingle?.currency))
                      : "-"
                    const priceSumText = priceSum
                      ? formatPrice(isoLocale, Number(priceSum?.value), String(priceSum?.currency))
                      : "-"
                    return (
                      <div key={idx} className="grid grid-cols-[1fr_120px_120px] items-center gap-4 px-3 py-3">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{title}</div>
                          <div className="text-xs text-gray-600">
                            {sku ? `${t("approvals.detail.items_header.product_id")} ${sku}` : null}
                            {qty ? `  ·  ${t("approvals.detail.items_header.quantity")} ${qty}` : null}
                          </div>
                        </div>
                        <div className="text-right text-sm">{priceSingleText}</div>
                        <div className="text-right text-sm">{priceSumText}</div>
                      </div>
                    )
                  })}
                </div>
              )
            }

            const cnt = Array.isArray(data.lineItems) ? data.lineItems.length : 0
            return <p className="p-3 text-sm">{cnt}</p>
          })()}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]">
        <div />
        <div className="rounded-sm border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-base font-semibold">{t("approvals.detail.summary.title")}</h3>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span>{t("approvals.detail.summary.items_net")}</span>
              <span>
                {itemTotalNet?.value != null && itemTotalNet?.currency
                  ? formatPrice(isoLocale, Number(itemTotalNet.value), String(itemTotalNet.currency))
                  : "-"}
              </span>
            </div>
            <div className="flex items-center justify-between text-gray-700">
              <span>{t("approvals.detail.summary.tax")}</span>
              <span>{mwstText ?? "-"}</span>
            </div>
            <div className="flex items-center justify-between text-gray-700">
              <span>{t("approvals.detail.summary.shipping_estimated")}</span>
              <span>
                {shipping?.gross?.value != null
                  ? formatPrice(isoLocale, Number(shipping.gross.value), String(shipping.gross.currency))
                  : shipping?.net?.value != null
                  ? formatPrice(isoLocale, Number(shipping.net.value), String(shipping.net.currency))
                  : t("approvals.detail.summary.shipping_free")}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between font-semibold">
              <span>{t("approvals.detail.summary.total_gross")}</span>
              <span>
                {grand?.gross?.value != null && grand?.gross?.currency
                  ? formatPrice(isoLocale, Number(grand.gross.value), String(grand.gross.currency))
                  : grand?.gross?.value != null && grand?.gross?.currency
                  ? formatPrice(isoLocale, Number(grand.gross.value), String(grand.gross.currency))
                  : data?.totalGross?.value != null && data?.totalGross?.currency
                  ? formatPrice(isoLocale, Number(data.totalGross.value), String(data.totalGross.currency))
                  : "-"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom actions under totals */}
      {(isPending && canManageApproval) && (
      <div className="mt-3 flex flex-wrap justify-end gap-3">
        <Button type="button" variant="outline" disabled={pending || !canManageApproval} onClick={() => setRejectOpen(true)}>
          {pending ? "…" : t("approvals.detail.actions.reject")}
        </Button>
        <form action={onApprove} onSubmit={(e) => { if (!canManageApproval) { e.preventDefault() } }}>
          <input type="hidden" name="customerNo" value={String(props.accountContext.customerData?.customerNo || "")} />
          <input type="hidden" name="userLogin" value={String(props.accountContext.userData?.email || props.accountContext.userData?.login || "")} />
          <input type="hidden" name="requisitionId" value={String(data?.id || data?.requisitionNo || "")} />
          <Button type="submit" disabled={pending || !canManageApproval}>
            {pending ? "…" : t("approvals.detail.actions.approve")}
          </Button>
        </form>
      </div>
      )}

      {/* Reject confirm dialog with required comment */}
      {isPending && canManageApproval && (
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("approvals.detail.actions.confirm_reject_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("approvals.detail.actions.confirm_reject_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder={t("approvals.detail.actions.comment_placeholder")}
            className="min-h-24"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>{t("approvals.detail.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={pending || !rejectComment.trim()} onClick={confirmReject}>
              {t("approvals.detail.actions.reject")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}
    </div>
  )
}
